/**
 * user.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, Subscription } from "rxjs/Rx";

import { BuildFlags } from "../../configuration/build-flags";
import { MessageReceiver } from "../../server/message-receiver";
import {
  AcknowledgmentResponse,
  Message,
  MessageTag,
} from "../../server/messages/general/";
import {
  GroupUsersRequest,
  GroupUsersResponse,
  UserLoginData,
  UserRequest,
  UserResponse,
} from "../../server/messages/group-management/";
import {
  UserConfigurationResponse,
  ZoneConfigurationResponse,
} from "../../server/messages/metadata/";
import { MessagingService } from "../../server/messaging.service";
import {
  disconnectDo,
  disconnectWith,
  disconnectValue,
  disconnectEmpty,
} from "../../support/types/decorators";

import { Group } from "./group";
import { Self, User } from "./user";
import {
  ActiveSessionsResponse,
  TerminateActiveSessionsRequest,
} from "app/server/messages/auth";
import { AuthenticatedChannelDetails } from "app/server/messages/admin/clients-status-response";
import {
  ChangeUserActiveStatusRequest,
  GroupActiveMap,
} from "app/server/messages/group-management/change-user-active-status-request";
import {
  EditOtherUserRequest,
  EditUserRequest,
} from "app/server/messages/group-management/edit-user-request";
import { Rule } from "../permissions/rules";
import { ZoneData } from "./zone";
import { CreateUserRequest } from "app/server/messages/group-management/create-user-request";
import { Hasher } from "../hasher.service";
import { Salter } from "../salter.service";
import { SetUserGroupsRequest } from "app/server/messages/group-management/set-user-group-request";
import { RemoveUserGroupsRequest } from "app/server/messages/group-management/remove-user-group-request";

const UNAUTHENTICATED_USER: Self = new Self("unauthenticated");

@Injectable()
export class UserService implements MessageReceiver {
  /** The currently-authenticated user. */
  @disconnectValue(UNAUTHENTICATED_USER)
  public self: Self = UNAUTHENTICATED_USER;

  /**
   * Construct a new {@link IntakeStepService}.
   */
  public constructor(
    protected messaging: MessagingService,
    protected router: Router,
    public hasher : Hasher,
    public salter : Salter,
  ) {
    this.messaging.ready.subscribe((on: boolean) =>
      on ? this.connected() : this.disconnected()
    );
  }

  @disconnectWith("next", false)
  public ready: BehaviorSubject<boolean> = new BehaviorSubject(false);

  @disconnectWith("unsubscribe")
  private subscription: Subscription = new Subscription();

  @disconnectEmpty()
  public languageCodes: string[] = [];

  @disconnectEmpty()
  public countryCodes: string[] = [];

  @disconnectEmpty()
  public otherSessions: AuthenticatedChannelDetails[] = [];

  public connected(): void {
    this.subscription = this.messaging
      .receive(MessageTag.RSP_CFG_USER)
      .subscribe((message: UserConfigurationResponse) => {
        this.self = UserConfigurationResponse.CreateUser(message);
        this.ready.next(true);
      });

    this.subscription.add(
      this.messaging
        .receive(MessageTag.RSP_CFG_ZONE)
        .subscribe((msg: ZoneConfigurationResponse) => {
          msg.availableLanguageCodes.forEach(
            ({ languageCode, id }: { languageCode: string; id: number }) =>
              (this.languageCodes[id] = languageCode)
          );
          msg.availableCountryCodes.forEach(
            ({ countryCode, id }: { countryCode: string; id: number }) =>
              (this.countryCodes[id] = countryCode)
          );
        })
    );

    this.subscription.add(
      this.messaging
        .receive(MessageTag.RSP_ACTIVE_SESSIONS)
        .subscribe((msg: ActiveSessionsResponse) => {
          this.otherSessions = msg.channels;
        })
    );
  }

  /**
   * Teardown instructions for when the {@link MessagingService} has stopped.
   */
  public disconnected(): void {
    /* decorator-generated */
  }

  public get debugMode(): boolean {
    return BuildFlags.debugMode || (this.self && this.self.dev);
  }

  public undebug(): void {
    BuildFlags.debugMode = false;
    if (this.self) {
      this.self.dev = false;
    }
    console.error(
      "Debug mode has been manually disabled. You must reload to enter again"
    );
  }

  public getGroupUsers(group: Group): Observable<Group> {
    const o: Observable<Message> = this.messaging.converse(
      new GroupUsersRequest(group.id)
    );

    return o.map((gur: GroupUsersResponse) => {
      group.users = gur.resultSet.map((uld: UserLoginData) =>
        User.fromManagementRecord(uld)
      );
      // TODO: deduplicate this behavior in favor of the boolean
      // isCurrent on UserResponse, which is more correct
      const selfIndex: number = group.users.findIndex(
        (u: User) =>
          u.login.toLocaleUpperCase() === this.self.login.toLocaleUpperCase()
      );
      if (selfIndex === -1) {
        console.error("Could not find own entry in users table!");
      } else {
        this.self.managementId = group.users[selfIndex].managementId;
        group.users[selfIndex] = this.self;
      }
      console.log("RUNNING HERE");
      console.log(group);
      return group;
    });
  }

  public requestUserDetails(user: User): Observable<boolean> {
    const o: Observable<Message> = this.messaging.converse(
      new UserRequest(user.managementId)
    );
    o.subscribe((rsp: UserResponse) => {
      console.log(rsp);
      user.extendWithUserResponse(rsp, this.languageCodes, this.countryCodes);
    });
    return o.map(() => true);
  }

  public requestUserCreate(
    login: string,
    password: string,
    name: string,
    phone: string,
    countryCodeIndex: number,
    languageCodeIndex: number,
    group: Group,
    groupRules: Rule[],
    zoneRules: Rule[]
  ): Observable<Message> {
    const groups = [
      {
        groupId: group.id,
        rules: groupRules,
      },
    ];
    const zones = this.self.zones.map((zoneData: ZoneData) => {
      const zoneMap = {
        zoneId: zoneData.zoneId,
        rules: zoneRules,
      };
      return zoneMap;
    });
    const o: Observable<Message> = this.messaging.converse(
      new CreateUserRequest(
        login,
        this.hasher.hexOf(this.salter.salt(password)),
        name,
        phone,
        countryCodeIndex,
        languageCodeIndex,
        groups,
        zones
      )
    );
    return o;
  }

  public requestUserActiveStatusChange(
    group: Group,
    user: User,
    isActive: boolean
  ): Observable<Message> {
    const groupActiveMap: GroupActiveMap = {
      groupId: group.id,
      status: isActive,
    };

    const message: ChangeUserActiveStatusRequest =
      new ChangeUserActiveStatusRequest(user.managementId, [groupActiveMap]);
    const o: Observable<Message> = this.messaging.converse(message);
    return o;
  }

  public requestUserEditRequest(
    name: string,
    phone: string,
    countryCodeID: number,
    languageCodeID: number
  ): Observable<Message> {
    const o: Observable<Message> = this.messaging.converse(
      new EditUserRequest(
        name,
        phone,
        this.countryCodes[countryCodeID],
        this.languageCodes[languageCodeID]
      )
    );
    return o;
  }

  public requestOtherUserEditRequest(
    objectId: number,
    name: string,
    phone: string,
    countryCodeID: number,
    languageCodeID: number
  ): Observable<Message> {
    const o: Observable<Message> = this.messaging.converse(
      new EditOtherUserRequest(
        objectId,
        name,
        phone,
        this.countryCodes[countryCodeID],
        this.languageCodes[languageCodeID]
      )
    );
    return o;
  }

  public requestAddUserToGroupRequest(
    objectId: number,
    group: Group,
    groupRules: Rule[],
  ): Observable<Message> {
    const o: Observable<Message> = this.messaging.converse(
      new SetUserGroupsRequest(
        [{
          groupId: group.id,
          rules: groupRules
        }],
        objectId,
      )
    );
    return o;
  }

  public requestRemoveUserGroupsRequest(
    objectId: number,
    groups: number[],
  ): Observable<Message> {
    const o: Observable<Message> = this.messaging.converse(
      new RemoveUserGroupsRequest(
        groups,
        objectId,
      )
    );
    return o;
  }

  public killAllSessions(includeSelf: boolean): Observable<Message> {
    return this.messaging.send(new TerminateActiveSessionsRequest(includeSelf));
  }

  // TODO: move this to external file
  public static readonly KnownIPs: Array<[RegExp, string]> = [
    [/127\.0\.0\.1/, "localhost"],
    [/24\.196\.96\.13\d/, "ABC Madison office"],
    [/72\.201\.25\.\d{1,3}/, "known remote: rlavigne"],
    [/67\.149\.\d{1,3}\.\d{1,3}/, "known remote: tdsmith"],
  ];

  public getIPCommonName(ipAddress: string): string | null {
    for (const pair of UserService.KnownIPs) {
      if (pair[0].test(ipAddress)) {
        return pair[1];
      }
    }
    return "unknown location";
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/auth/identity/user.service.ts